import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Users, MapPin as MapIcon, Building2, ArrowRight, ExternalLink, Newspaper } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Header } from './Header'
import AutoGrid from './AutoGrid'
import SideMenu from './SideMenu'
import Footer from './Footer'
import DistrictMap from './DistrictMap'
import { AMBITI_STATS, TOTALI_BACINO, type AmbitoId } from '../data/ambitiStats'
import { DISTRETTI } from '../data/geodata'
import { pianiDiZona, CLUSTER_TARGET_VALUES, type ClusterTarget } from '../data/pianiDiZona'
import { supabase } from '../integrations/supabase/client'
import BSDCard from './BSDCard'
import SectionBadge from './SectionBadge'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, LineChart, Line, CartesianGrid } from 'recharts'

/* ── helpers ─────────────────────────────────────────────── */

function formatInt(value: number) {
  return new Intl.NumberFormat('it-IT').format(value)
}

function formatDecimalWithThousands(value: number) {
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: true }).format(value)
}

function formatDecimal(value: number) {
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)
}

const CHART_TOOLTIP_STYLE = {
  wrapperStyle: { zIndex: 80 },
  contentStyle: { background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)' },
}

/* ── Indice di Vecchiaia data ── */
const INDICE_VECCHIAIA_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [{ anno: '2003', valore: 121.1 },{ anno: '2007', valore: 128.7 },{ anno: '2011', valore: 130.6 },{ anno: '2015', valore: 142.3 },{ anno: '2019', valore: 158.0 },{ anno: '2025', valore: 196.1 }],
  desio: [{ anno: '2003', valore: 112.4 },{ anno: '2007', valore: 122.3 },{ anno: '2011', valore: 125.5 },{ anno: '2015', valore: 137.1 },{ anno: '2019', valore: 151.4 },{ anno: '2025', valore: 181.3 }],
  monza: [{ anno: '2003', valore: 140.7 },{ anno: '2007', valore: 154.8 },{ anno: '2011', valore: 159.0 },{ anno: '2015', valore: 172.1 },{ anno: '2019', valore: 182.8 },{ anno: '2025', valore: 207.5 }],
  seregno: [{ anno: '2003', valore: 126.1 },{ anno: '2007', valore: 134.9 },{ anno: '2011', valore: 133.6 },{ anno: '2015', valore: 142.1 },{ anno: '2019', valore: 156.5 },{ anno: '2025', valore: 191.7 }],
  vimercate: [{ anno: '2003', valore: 113.0 },{ anno: '2007', valore: 123.1 },{ anno: '2011', valore: 124.4 },{ anno: '2015', valore: 137.0 },{ anno: '2019', valore: 153.3 },{ anno: '2025', valore: 186.8 }],
}
const INDICE_VECCHIAIA_SERIES_ASST: Array<{ anno: string; valore: number }> = [{ anno: '2003', valore: 119.7 },{ anno: '2007', valore: 128.6 },{ anno: '2011', valore: 129.3 },{ anno: '2015', valore: 140.3 },{ anno: '2019', valore: 159.9 },{ anno: '2025', valore: 192.1 }]

/* ── Indice di Dipendenza data ── */
const INDICE_DIPENDENZA_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [{ anno: '2003', valore: 45.0 },{ anno: '2007', valore: 49.2 },{ anno: '2011', valore: 51.4 },{ anno: '2015', valore: 54.0 },{ anno: '2019', valore: 55.8 },{ anno: '2025', valore: 56.5 }],
  desio: [{ anno: '2003', valore: 43.6 },{ anno: '2007', valore: 48.2 },{ anno: '2011', valore: 50.0 },{ anno: '2015', valore: 52.9 },{ anno: '2019', valore: 55.0 },{ anno: '2025', valore: 55.8 }],
  monza: [{ anno: '2003', valore: 46.6 },{ anno: '2007', valore: 53.5 },{ anno: '2011', valore: 57.6 },{ anno: '2015', valore: 60.1 },{ anno: '2019', valore: 60.5 },{ anno: '2025', valore: 59.2 }],
  seregno: [{ anno: '2003', valore: 43.8 },{ anno: '2007', valore: 47.8 },{ anno: '2011', valore: 50.3 },{ anno: '2015', valore: 53.8 },{ anno: '2019', valore: 56.0 },{ anno: '2025', valore: 57.2 }],
  vimercate: [{ anno: '2003', valore: 43.3 },{ anno: '2007', valore: 47.8 },{ anno: '2011', valore: 50.3 },{ anno: '2015', valore: 54.6 },{ anno: '2019', valore: 56.8 },{ anno: '2025', valore: 56.4 }],
}
const INDICE_DIPENDENZA_SERIES_ASST: Array<{ anno: string; valore: number }> = [{ anno: '2003', valore: 43.7 },{ anno: '2007', valore: 47.9 },{ anno: '2011', valore: 50.3 },{ anno: '2015', valore: 53.8 },{ anno: '2019', valore: 56.8 },{ anno: '2025', valore: 57.0 }]

function SchedeBarTooltip({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  return (
    <div style={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)', padding: '8px 10px' }}>
      <div style={{ fontWeight: 600 }}>{p?.payload?.name ?? ''}</div>
      <div style={{ color: 'var(--text-2)' }}>Schede: {formatInt(Number(p?.value ?? 0))}</div>
    </div>
  )
}

const AMBITO_TO_PDZ_KEY: Record<AmbitoId, string> = { desio: 'Desio', vimercate: 'Vimercate', monza: 'Monza', seregno: 'Seregno', carate: 'Carate' }

/* ── news helpers ────────────────────────────────────────── */

interface NewsItem { id: string; title: string; url: string; publish_date: string | null; scraped_at: string; source: string }

const SOURCE_LABELS: Record<string, { label: string; color: string; baseUrl: string }> = {
  'asst-brianza': { label: 'ASST Brianza', color: '#2563eb', baseUrl: 'https://www.asst-brianza.it' },
  'csv-mb-territorio': { label: 'Territorio', color: '#059669', baseUrl: '' },
  'csv-mb-cittadini': { label: 'Cittadini', color: '#d97706', baseUrl: '' },
  'csv-mb-org-notizie': { label: 'Org. Notizie', color: '#7c3aed', baseUrl: '' },
  'csv-mb-org-bandi': { label: 'Org. Bandi', color: '#dc2626', baseUrl: '' },
  'csv-mb-org-corsi': { label: 'Org. Corsi', color: '#0284c7', baseUrl: '' },
}

function getFullUrl(item: NewsItem) {
  return item.url.startsWith('http') ? item.url : (SOURCE_LABELS[item.source]?.baseUrl ?? '') + item.url
}

function formatNewsSourceLabel(source: string) {
  if (source.startsWith('csv-')) return `CSV ${SOURCE_LABELS[source]?.label ?? source}`
  return SOURCE_LABELS[source]?.label ?? source
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
}

/* ── main component ──────────────────────────────────────── */

export default function DashboardPage() {
  const [sideMenuOpen, setSideMenuOpen] = useState(false)
  const [selectedAmbito, setSelectedAmbito] = useState<AmbitoId | null>(null)
  const [news, setNews] = useState<NewsItem[]>([])
  const [newsLoading, setNewsLoading] = useState(true)
  const [activeNewsIndex, setActiveNewsIndex] = useState(0)
  const [isMobileNewsCard, setIsMobileNewsCard] = useState(false)
  const newsScrollRef = useRef<HTMLDivElement | null>(null)

  const CSV_SOURCES = ['csv-mb-territorio', 'csv-mb-cittadini', 'csv-mb-org-notizie', 'csv-mb-org-bandi', 'csv-mb-org-corsi'] as const
  const [csvNews, setCsvNews] = useState<Record<string, NewsItem[]>>({})
  const [csvNewsLoading, setCsvNewsLoading] = useState(true)
  const [csvActiveIndex, setCsvActiveIndex] = useState<Record<string, number>>({})

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('news').select('*').eq('source', 'asst-brianza').order('publish_date', { ascending: false }).limit(3)
      if (data) setNews(data as NewsItem[])
      setNewsLoading(false)
    })()
  }, [])

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('news').select('*').in('source', [...CSV_SOURCES]).order('publish_date', { ascending: false })
      if (data) {
        const grouped: Record<string, NewsItem[]> = {}
        for (const src of CSV_SOURCES) grouped[src] = []
        for (const item of data as NewsItem[]) {
          if (grouped[item.source]) grouped[item.source].push(item)
        }
        // Limit to 3 per source
        for (const src of CSV_SOURCES) grouped[src] = grouped[src].slice(0, 3)
        setCsvNews(grouped)
      }
      setCsvNewsLoading(false)
    })()
  }, [])

  // Auto-rotate CSV news cards with staggered random intervals
  useEffect(() => {
    const timers: number[] = []
    const scheduleNext = (src: string) => {
      const delay = 3500 + Math.random() * 4000 // 3.5s–7.5s
      const timer = window.setTimeout(() => {
        setCsvActiveIndex(prev => {
          const count = csvNews[src]?.length ?? 0
          if (count <= 1) return prev
          return { ...prev, [src]: ((prev[src] ?? 0) + 1) % count }
        })
        scheduleNext(src)
      }, delay)
      timers.push(timer)
    }
    for (const src of CSV_SOURCES) {
      const count = csvNews[src]?.length ?? 0
      if (count > 1) scheduleNext(src)
    }
    return () => timers.forEach(t => window.clearTimeout(t))
  }, [csvNews])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(max-width: 768px)')
    const updateIsMobile = () => setIsMobileNewsCard(mediaQuery.matches)
    updateIsMobile()
    mediaQuery.addEventListener('change', updateIsMobile)
    return () => mediaQuery.removeEventListener('change', updateIsMobile)
  }, [])

  useEffect(() => {
    setActiveNewsIndex(0)
  }, [news])

  useEffect(() => {
    if (news.length <= 1) return
    let timer: number
    const scheduleNext = () => {
      const delay = 3500 + Math.random() * 4000
      timer = window.setTimeout(() => {
        setActiveNewsIndex(prev => (prev + 1) % news.length)
        scheduleNext()
      }, delay)
    }
    scheduleNext()
    return () => window.clearTimeout(timer)
  }, [news.length])

  useEffect(() => {
    if (!isMobileNewsCard) return
    const container = newsScrollRef.current
    if (!container) return
    const cardHeight = container.clientHeight
    container.scrollTo({ top: activeNewsIndex * cardHeight, behavior: 'smooth' })
  }, [activeNewsIndex, isMobileNewsCard])

  const handleSelectDistrict = useCallback((id: string) => {
    setSelectedAmbito(id as AmbitoId)
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedAmbito(null)
  }, [])

  /* demographic data */
  const data = useMemo(() => {
    if (selectedAmbito) {
      const a = AMBITI_STATS.find(x => x.id === selectedAmbito)!
      return { titolo: `Ambito di ${a.nome}`, ...a }
    }
    return { titolo: 'ASST Brianza', id: null as any, nome: 'ASST Brianza', ...TOTALI_BACINO, maschi: TOTALI_BACINO.maschi, femmine: TOTALI_BACINO.femmine, comuni: [] as string[] }
  }, [selectedAmbito])

  /* indice vecchiaia */
  const indiceVecchiaiaData = useMemo(() => {
    const serie = selectedAmbito ? INDICE_VECCHIAIA_SERIES_BY_AMBITO[selectedAmbito] : INDICE_VECCHIAIA_SERIES_ASST
    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    return { valore2025, serie }
  }, [selectedAmbito])

  /* carico sociale (indice dipendenza) */
  const indiceDipendenzaData = useMemo(() => {
    const serie = selectedAmbito ? INDICE_DIPENDENZA_SERIES_BY_AMBITO[selectedAmbito] : INDICE_DIPENDENZA_SERIES_ASST
    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    return { valore2025, serie }
  }, [selectedAmbito])

  const pdzData = useMemo(() => {
    const pianiAttivi = selectedAmbito
      ? (() => { const p = pianiDiZona[AMBITO_TO_PDZ_KEY[selectedAmbito]]; return p ? [p] : [] })()
      : Object.values(pianiDiZona).filter((p): p is NonNullable<typeof p> => p !== null)

    const clusterColorMap: Record<ClusterTarget, string> = { Adulti: '#3b82f6', Minori: '#10b981', Anziani: '#f59e0b' }
    const clusterMap = new Map(CLUSTER_TARGET_VALUES.map(c => [c, { id: c.toLowerCase(), name: c, count: 0, color: clusterColorMap[c] }]))
    const gruppiMap = new Map<string, { id: string; name: string; schedeCount: number; azioniCount: number; color: string }>()
    let totaleSchede = 0, totaleAzioni = 0

    for (const piano of pianiAttivi) {
      for (const gruppo of piano.gruppi) {
        const sc = gruppo.schede.length
        const az = gruppo.schede.reduce((a, s) => a + s.azioni.length, 0)
        totaleSchede += sc; totaleAzioni += az
        const prev = gruppiMap.get(gruppo.id)
        if (prev) { prev.schedeCount += sc; prev.azioniCount += az }
        else gruppiMap.set(gruppo.id, { id: gruppo.id, name: gruppo.titolo, schedeCount: sc, azioniCount: az, color: gruppo.colore })
        for (const scheda of gruppo.schede) {
          for (const key of new Set(scheda.target_cluster ?? [])) { const c = clusterMap.get(key); if (c) c.count += 1 }
        }
      }
    }

    return {
      totaleSchede,
      totaleAzioni,
      schedePerCluster: Array.from(clusterMap.values()).sort((a, b) => b.count - a.count),
      azioniPerGruppo: Array.from(gruppiMap.values()).map(g => ({ id: g.id, name: g.name, value: g.azioniCount, color: g.color })).sort((a, b) => b.value - a.value),
    }
  }, [selectedAmbito])

  const comuniList = useMemo(() => {
    const source = selectedAmbito
      ? data.comuni
      : Object.values(DISTRETTI).flatMap(distretto => distretto.comuni)

    return Array.from(new Set(source)).sort((a, b) => a.localeCompare(b, 'it'))
  }, [selectedAmbito, data.comuni])

  const selectedLabel = selectedAmbito ? AMBITI_STATS.find(a => a.id === selectedAmbito)?.nome ?? '' : 'Tutti gli ambiti'

  return (
    <div className="app">
      <Header onMenuOpen={() => setSideMenuOpen(true)} />
      <SideMenu open={sideMenuOpen} onClose={() => setSideMenuOpen(false)} theme="dark" onToggleTheme={() => {}} />

      <div className="app-body">
        <div className="app-content">
          <section className="telemetry-dashboard">
            <div className="telemetry-inner">

              {/* ── MAP + DEMOGRAPHICS ── */}
              <BSDCard kind="panel" sezione="Dati demografici" style={{ marginBottom: 0 }}>
                <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>Dashboard</h3>
              </BSDCard>

              {/* ── NEWS ROW ── */}
              <AutoGrid style={{ alignItems: 'stretch' }}>
                <BSDCard kind="kpi" sezione="Notizie" style={{ minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8 }}>
                  {newsLoading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Caricamento…</span>
                    </div>
                  ) : news.length === 0 ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Nessuna news</span>
                    </div>
                  ) : isMobileNewsCard ? (
                    <div
                      ref={newsScrollRef}
                      onScroll={(event) => {
                        const target = event.currentTarget
                        const cardHeight = target.clientHeight || 1
                        const nextIndex = Math.round(target.scrollTop / cardHeight)
                        if (nextIndex !== activeNewsIndex && nextIndex >= 0 && nextIndex < news.length) setActiveNewsIndex(nextIndex)
                      }}
                      style={{ flex: 1, overflowY: 'auto', scrollSnapType: 'y mandatory', scrollbarWidth: 'none' }}
                    >
                      {news.map(item => (
                        <a
                          key={item.id}
                          href={getFullUrl(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'stretch',
                            minHeight: 96,
                            scrollSnapAlign: 'start',
                            color: 'inherit',
                            marginBottom: 4,
                          }}
                        >
                          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 6 }}>
                            <div className="telemetry-kpi-head" style={{ alignItems: 'flex-start' }}>
                              <div style={{ minWidth: 0 }}>
                                <div className="telemetry-kpi-label">{formatNewsSourceLabel(item.source)}</div>
                              </div>
                              <div className="telemetry-kpi-icon" style={{ backgroundColor: '#2563eb20', flexShrink: 0 }}>
                                <Newspaper size={16} style={{ color: '#2563eb' }} />
                              </div>
                            </div>
                            <span className="telemetry-kpi-sub">{item.title}</span>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{formatDate(item.publish_date)}</span>
                              <ExternalLink size={12} style={{ color: 'var(--text-2)' }} />
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <a href={getFullUrl(news[activeNewsIndex])} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex', flex: 1 }}>
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 6, color: 'inherit' }}>
                        <div className="telemetry-kpi-head" style={{ alignItems: 'flex-start' }}>
                          <div style={{ minWidth: 0 }}>
                            <div className="telemetry-kpi-label">{formatNewsSourceLabel(news[activeNewsIndex]?.source ?? 'asst-brianza')}</div>
                          </div>
                          <div className="telemetry-kpi-icon" style={{ backgroundColor: '#2563eb20', flexShrink: 0 }}>
                            <Newspaper size={16} style={{ color: '#2563eb' }} />
                          </div>
                        </div>
                        <span className="telemetry-kpi-sub">
                          {news[activeNewsIndex]?.title}
                        </span>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{formatDate(news[activeNewsIndex]?.publish_date ?? null)}</span>
                          <ExternalLink size={12} style={{ color: 'var(--text-2)' }} />
                        </div>
                      </div>
                    </a>
                  )}

                  {!newsLoading && news.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 2 }}>
                      {news.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          aria-label={`Mostra news ${index + 1}`}
                          onClick={() => setActiveNewsIndex(index)}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '9999px',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            background: index === activeNewsIndex ? 'var(--accent)' : 'var(--border)',
                          }}
                        />
                      ))}
                    </div>
                  )}
                </BSDCard>

                {Array.from({ length: 3 }).map((_, index) => (
                  <BSDCard key={`empty-news-${index}`} kind="kpi" sezione="Notizie" style={{ minHeight: 120 }} />
                ))}

                <Link to="/news" style={{ textDecoration: 'none', display: 'flex', flex: 1 }}>
                  <BSDCard kind="kpi" sezione="Notizie" style={{ background: 'linear-gradient(135deg, hsl(221 83% 53% / 0.15), hsl(221 83% 53% / 0.05))', border: '1px solid hsl(221 83% 53% / 0.3)', cursor: 'pointer', minHeight: 90, width: '100%', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
                    <Newspaper size={20} style={{ color: '#2563eb' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#2563eb' }}>Vedi tutte</span>
                    <ArrowRight size={18} style={{ color: '#2563eb' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-2)' }}>Notizie</span>
                  </BSDCard>
                </Link>
              </AutoGrid>

              {/* ── CSV MB NEWS ROW ── */}
              <AutoGrid style={{ alignItems: 'stretch' }}>
                {CSV_SOURCES.map(src => {
                  const items = csvNews[src] ?? []
                  const meta = SOURCE_LABELS[src]
                  const activeIdx = csvActiveIndex[src] ?? 0
                  const activeItem = items[activeIdx]
                  return (
                    <BSDCard key={src} kind="kpi" sezione="Notizie" style={{ minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8 }}>
                      {csvNewsLoading ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Caricamento…</span>
                        </div>
                      ) : items.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Nessuna news</span>
                        </div>
                      ) : (
                        <a href={activeItem?.url ?? '#'} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex', flex: 1 }}>
                          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 6, color: 'inherit' }}>
                            <div className="telemetry-kpi-head" style={{ alignItems: 'flex-start' }}>
                              <div style={{ minWidth: 0 }}>
                                <div className="telemetry-kpi-label">{formatNewsSourceLabel(src)}</div>
                              </div>
                              <div className="telemetry-kpi-icon" style={{ backgroundColor: `${meta?.color ?? '#666'}20`, flexShrink: 0 }}>
                                <Newspaper size={16} style={{ color: meta?.color ?? '#666' }} />
                              </div>
                            </div>
                            <span className="telemetry-kpi-sub">
                              {activeItem?.title}
                            </span>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{formatDate(activeItem?.publish_date ?? null)}</span>
                              <ExternalLink size={12} style={{ color: 'var(--text-2)' }} />
                            </div>
                          </div>
                        </a>
                      )}

                      {!csvNewsLoading && items.length > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 2 }}>
                          {items.map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => setCsvActiveIndex(prev => ({ ...prev, [src]: index }))}
                              style={{
                                width: 8, height: 8, borderRadius: '9999px', border: 'none', padding: 0, cursor: 'pointer',
                                background: index === activeIdx ? (meta?.color ?? 'var(--accent)') : 'var(--border)',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </BSDCard>
                  )
                })}
              </AutoGrid>

              <div className="dashboard-map-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, alignItems: 'start' }}>
                {/* Map – spans 2 columns */}
                <div className="telemetry-panel-card telemetry-map-card" style={{ padding: 12, gridColumn: 'span 2' }}>
                  <SectionBadge sezione="Dati demografici" />
                  <DistrictMap
                    onSelectDistrict={handleSelectDistrict}
                    onClearSelection={handleClearSelection}
                    selectedDistrictId={selectedAmbito}
                    showLegend={false}
                  />
                </div>

                {/* Demographics summary – spans 3 columns */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, gridColumn: 'span 3' }}>
                  <div className="telemetry-kpi-grid dashboard-demo-subgrid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                    <BSDCard kind="kpi" sezione="Dati demografici" label="Popolazione" value={formatInt(data.popolazione)} icon={Users} color="#0ea5e9"
                      sub={<>Donne: <b>{formatInt(data.femmine)}</b><br />Uomini: <b>{formatInt(data.maschi)}</b></>} />
                    <BSDCard
                      kind="kpi"
                      sezione="Dati demografici"
                      style={{ display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}
                    >
                      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        <div style={{ position: 'absolute', inset: -20, opacity: 0.3, filter: 'saturate(0.85) contrast(0.9)' }}>
                          <DistrictMap
                            onSelectDistrict={() => {}}
                            selectedDistrictId={selectedAmbito ?? null}
                            allowDistrictSelection={false}
                            showLegend={false}
                            showInstructionText={false}
                          />
                        </div>
                      </div>

                      <div className="telemetry-kpi-head" style={{ position: 'relative', zIndex: 1 }}>
                        <div>
                          <div className="telemetry-kpi-label">Superficie</div>
                          <p className="telemetry-kpi-value">{`${formatInt(data.km2)} Km²`}</p>
                        </div>
                        <div className="telemetry-kpi-icon" style={{ backgroundColor: '#f59e0b20', flexShrink: 0 }}>
                          <MapIcon size={20} style={{ color: '#f59e0b' }} />
                        </div>
                      </div>

                      <div className="telemetry-kpi-line" style={{ backgroundColor: '#f59e0b', zIndex: 1 }} />
                    </BSDCard>
                    <div className="dashboard-comuni-vedi" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <BSDCard
                        kind="kpi"
                        sezione="Dati demografici"
                        label="Comuni"
                        value={formatInt(data.numeroComuni)}
                        icon={Building2}
                        color="#8b5cf6"
                        sub={`Comuni: ${comuniList.join(', ')}`}
                        style={{ gridColumn: 'span 2' }}
                      />
                    </div>
                  </div>
                  <div className="telemetry-kpi-grid dashboard-demo-subgrid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                    <BSDCard kind="kpi" sezione="Dati demografici" label="Carico Sociale" value={formatDecimal(indiceDipendenzaData.valore2025)} icon={Users} color="#dc2626"
                      sub={`Indice di dipendenza strutturale`} />
                    <BSDCard kind="panel" sezione="Dati demografici" zoomable>
                      <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione carico sociale</h3>
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={indiceDipendenzaData.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                          <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={34} />
                          <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatDecimal(Number(value ?? 0)), 'Indice']} />
                          <Line type="monotone" dataKey="valore" stroke="#dc2626" strokeWidth={2.2} dot={{ r: 2.4 }} activeDot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </BSDCard>
                    <BSDCard kind="panel" sezione="Dati demografici" zoomable>
                      <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione indice di vecchiaia</h3>
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={indiceVecchiaiaData.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                          <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                          <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatDecimal(Number(value ?? 0)), 'Indice']} />
                          <Line type="monotone" dataKey="valore" stroke="#2563eb" strokeWidth={2.2} dot={{ r: 2.4 }} activeDot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </BSDCard>
                  </div>
                </div>
              </div>


              {/* ── PIANO DI ZONA SUMMARY ── */}
              <AutoGrid>
                <BSDCard kind="kpi" sezione="Piano di Zona" style={{ background: 'linear-gradient(135deg, hsl(262 83% 58% / 0.15), hsl(262 83% 58% / 0.05))', border: '1px solid hsl(262 83% 58% / 0.25)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6, padding: '14px 16px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', lineHeight: 1.3 }}>Interventi e servizi socio-assistenziali</div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>Piano di Zona 2025–2027: obiettivi, azioni e beneficiari programmati dagli Ambiti Territoriali.</div>
                </BSDCard>
                <BSDCard kind="kpi" sezione="Piano di Zona" label="Obiettivi" value={formatInt(pdzData.totaleSchede)} icon={Users} color="#7c3aed"
                  sub="Risultati attesi del Piano di Zona" />
                <BSDCard kind="panel" sezione="Piano di Zona" zoomable>
                  <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Beneficiari</h3>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={pdzData.schedePerCluster} layout="vertical" margin={{ top: 4, right: 22, bottom: 4, left: 10 }}>
                      <XAxis type="number" hide allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-2)' }} axisLine={false} tickLine={false} width={54} />
                      <Tooltip content={<SchedeBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={14}>
                        <LabelList dataKey="count" position="right" formatter={v => formatInt(Number(v ?? 0))} fill="var(--text-2)" fontSize={11} />
                        {pdzData.schedePerCluster.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </BSDCard>
                <BSDCard kind="kpi" sezione="Piano di Zona" label="Azioni" value={formatInt(pdzData.totaleAzioni)} icon={Building2} color="#0284c7"
                  sub="Attività concrete per raggiungere gli obiettivi" />
                <Link to="/pianidizona" style={{ textDecoration: 'none' }}>
                  <BSDCard kind="kpi" sezione="Piano di Zona" style={{ background: 'linear-gradient(135deg, hsl(262 83% 58% / 0.18), hsl(262 83% 58% / 0.06))', border: '1px solid hsl(262 83% 58% / 0.3)', cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#7c3aed' }}>Vedi tutti</span>
                    <ArrowRight size={18} style={{ color: '#7c3aed' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-2)' }}>Piani di Zona</span>
                  </BSDCard>
                </Link>
              </AutoGrid>


            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  )
}
